import type { ComponentType } from "react";

import type { SignalType } from "app/pages/sales-intelligence/domain/signals/types/signal";
import { signalTypeMap } from "app/pages/sales-intelligence/domain/signals/types/signal";
import { createSignalTypeLockedView } from "app/pages/sales-intelligence/modules/signals/components/SignalTypeLockedView/SignalTypeLockedView";
import {
    requestAppsSignalsAccess,
    requestIntentSignalsAccess,
} from "app/pages/sales-intelligence/modules/signals/services/requestSignalTypeAccess";
import type SignalTypeListEntry from "app/pages/sales-intelligence/sub-modules/signals/models/view/SignalTypeListEntry";

import type { LeadsSearchFilterInstance } from "../../types/leadsSearchFilterInstance";

/**
 * The upgrade surface a locked signal type shows in place of its picker -- per type, because each one
 * sells a different capability. `lockedGateViews.spec` pins this against the types the signals service
 * can actually lock, so a newly lockable type cannot silently inherit another type's upsell.
 */
const lockedGateViewMap: Partial<Record<SignalType, ComponentType>> = {
    [signalTypeMap.apps]: createSignalTypeLockedView({
        signalType: signalTypeMap.apps,
        onRequestAccess: requestAppsSignalsAccess,
    }),
    [signalTypeMap.intent]: createSignalTypeLockedView({
        signalType: signalTypeMap.intent,
        onRequestAccess: requestIntentSignalsAccess,
    }),
};

/** Exported for the guard spec so it need not reach into the map. */
export const lockedGateViewSignalTypes = Object.keys(lockedGateViewMap) as readonly SignalType[];

/**
 * A gate replaces the card body, so the signal picker and its "add signal" CTA never render for a type
 * the account is not entitled to.
 */
export const resolveLockedGateStatus = (entry: SignalTypeListEntry): LeadsSearchFilterInstance["gateStatus"] => {
    if (entry.lifecycleState !== "locked") {
        return undefined;
    }

    const view = lockedGateViewMap[entry.signalType];

    return view ? { type: "upgrade", view } : undefined;
};
